// pages/ChatSpace/ChatSpace.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Switch,
    FormControlLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ForumIcon from '@mui/icons-material/Forum';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ChatSpaceSidebar from './ChatSpaceSidebar';
import ChatSpaceInterface from './ChatSpaceInterface';
import ChatSpaceCitations from './ChatSpaceCitations';

export default function ChatSpace() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    // Layout state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Citations state
    const [citations, setCitations] = useState([]);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [sourcesCollapsed, setSourcesCollapsed] = useState(false);

    // Chat state
    const [currentSessionCode, setCurrentSessionCode] = useState(null);

    // Privacy state
    const [privacy, setPrivacy] = useState(true);

    const hasCitations = citations && citations.length > 0;

    const handleCitationsChange = (newCitations) => {
        setCitations(newCitations);
        if (newCitations.length > 0) {
            setSourcesOpen(true);
            setSourcesCollapsed(false);
        }
    };

    const handleCloseCitations = () => {
        setCitations([]);
        setSourcesOpen(false);
    };

    const handleConversationSelect = (sessionCode) => {
        if (!sessionCode) {
            setCurrentSessionCode(null);
        } else {
            setCurrentSessionCode(sessionCode);
        }
        setCitations([]);
        setSourcesOpen(false);
        setSidebarOpen(false);
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 144px)', width: '100%', bgcolor: 'grey.100', overflow: 'hidden', position: 'relative', p: 2, gap: 2, mt: 10 }}>

            {/* 1. LEFT SIDEBAR - Chat History (Desktop) */}
            {!isMobile && (
                <ChatSpaceSidebar
                    isOpen={true}
                    isCollapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                    onConversationSelect={handleConversationSelect}
                    currentSessionCode={currentSessionCode}
                />
            )}

            {/* Mobile Sidebar */}
            {isMobile && (
                <ChatSpaceSidebar
                    isOpen={sidebarOpen}
                    isCollapsed={false}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    onConversationSelect={handleConversationSelect}
                    currentSessionCode={currentSessionCode}
                    isMobile
                />
            )}

            {/* 2. MAIN CONTENT AREA */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>

                {/* Top Bar */}
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Mobile menu button */}
                        {isMobile && (
                            <IconButton size="small" onClick={() => setSidebarOpen(true)}>
                                <MenuIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            </IconButton>
                        )}

                        {/* Tab - General (active, single tab) */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: 'primary.main',
                                color: 'white',
                            }}
                        >
                            <ForumIcon sx={{ fontSize: 16 }} />
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                Chat Space
                            </Typography>
                        </Box>

                    </Box>

                    {/* Right side: Privacy toggle + Sources toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={privacy}
                                    onChange={(e) => setPrivacy(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="caption" color="text.secondary">
                                    Privacy
                                </Typography>
                            }
                            sx={{ ml: 0, mr: 0 }}
                        />
                        {hasCitations && (
                            <IconButton
                                size="small"
                                onClick={() => isMobile ? setSourcesOpen(!sourcesOpen) : setSourcesCollapsed(!sourcesCollapsed)}
                                title={sourcesCollapsed ? 'Expand sources' : 'Collapse sources'}
                            >
                                <ViewSidebarIcon
                                    sx={{
                                        fontSize: 20,
                                        color: 'grey.500',
                                        transform: sourcesCollapsed ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.3s',
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Chat Content */}
                <Box sx={{ flex: 1, overflow: 'hidden', borderRadius: 3 }}>
                    <ChatSpaceInterface
                        sessionCode={currentSessionCode}
                        onSessionChange={setCurrentSessionCode}
                        onCitationsChange={handleCitationsChange}
                        privacy={privacy}
                    />
                </Box>
            </Box>

            {/* 3. RIGHT SIDEBAR - Citations (Desktop) */}
            {hasCitations && !isMobile && (
                <Box
                    sx={{
                        width: sourcesCollapsed ? 56 : 240,
                        flexShrink: 0,
                        transition: 'width 0.3s',
                        height: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <ChatSpaceCitations
                        isOpen={true}
                        isCollapsed={sourcesCollapsed}
                        onToggle={() => setSourcesCollapsed(!sourcesCollapsed)}
                        onClose={handleCloseCitations}
                        citations={citations}
                    />
                </Box>
            )}

            {/* Mobile Citations Overlay */}
            {hasCitations && isMobile && (
                <ChatSpaceCitations
                    isOpen={sourcesOpen}
                    isCollapsed={false}
                    onToggle={() => setSourcesOpen(!sourcesOpen)}
                    onClose={() => { setSourcesOpen(false); handleCloseCitations(); }}
                    citations={citations}
                />
            )}
        </Box>
    );
}
